import {
  getGetNodoPortaleQueryKey,
  NodoDto,
  RegistroDto,
  useActionDispositivo,
  useGetRegistriDispositivo,
} from "@/api";
import { SmallLoading } from "@/components/Loading";
import Button from "@/elements/Button";
import { portaleConfig } from "@/portaliConfig";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toBool } from "../../utils";

export type GFL_TFLD_550_2_Action =
  | "start pv1"
  | "stop pv1"
  | "start s pt1"
  | "stop pt1"
  | "start rh1"
  | "stop rh1"
  | "start rh2"
  | "stop rh2"
  | "start ev1"
  | "stop ev1"
  | "start ev2"
  | "stop ev2"
  | "start auto"
  | "stop auto"
  | "reset";

// Utenze comandabili in manuale: per ognuna il topic info con lo stato e le due
// action di start/stop. I payload sono quelli del protocollo (vedi
// GattiFiltrazioni.TimeSeries/protocolli/GFL_TFLD_550_2).
const utenze: {
  label: string;
  statoInfo: string;
  start: GFL_TFLD_550_2_Action;
  stop: GFL_TFLD_550_2_Action;
}[] = [
  {
    label: "pompa vuoto PV1",
    statoInfo: "statopv1",
    start: "start pv1",
    stop: "stop pv1",
  },
  {
    label: "motopompa PT1",
    statoInfo: "statop1",
    start: "start s pt1",
    stop: "stop pt1",
  },
  {
    label: "resistenza RH1",
    statoInfo: "statorh1",
    start: "start rh1",
    stop: "stop rh1",
  },
  {
    label: "resistenza RH2",
    statoInfo: "statorh2",
    start: "start rh2",
    stop: "stop rh2",
  },
  {
    label: "elettrovalvola EV1",
    statoInfo: "statoev1",
    start: "start ev1",
    stop: "stop ev1",
  },
  {
    label: "elettrovalvola EV2",
    statoInfo: "statoev2",
    start: "start ev2",
    stop: "stop ev2",
  },
];

function registriMap(registri: RegistroDto[] | undefined) {
  return Object.fromEntries((registri ?? []).map((r) => [r.registro, r.value]));
}

export function GFL_TFLD_550_2_Buttons({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();
  const [sentAction, setSentAction] = useState<GFL_TFLD_550_2_Action | null>(
    null
  );
  const { data: registri } = useGetRegistriDispositivo(nodo.id);
  const { mutateAsync: setActionAsync, isLoading: actionLoading } =
    useActionDispositivo();
  const queryClient = useQueryClient();

  async function setAction(action: GFL_TFLD_550_2_Action) {
    try {
      setSentAction(action);
      const response = await setActionAsync({
        id: nodo.id,
        data: { action },
      });

      if (response.data?.updated) {
        toast.success(t("dispositivoActions.toast.success"));
      } else {
        toast.warn(t("dispositivoActions.toast.noResponse"));
      }
    } catch {
      toast.error(t("dispositivoActions.toast.error"));
    } finally {
      setSentAction(null);
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
    }
  }

  const status = (nodo.dispositivoLastStatus ?? "").trim().toLowerCase();
  const values = registriMap(registri?.data);
  // funzman = 1 => funzionamento automatico, come su GFL TH 4000
  const manualMode = !toBool(values.funzman);
  const info = nodo.dispositivoLastInfo ?? {};
  const statiUtenze = utenze.map((u) => toBool(info[u.statoInfo]));
  const anyUtenzaRunning = statiUtenze.some(Boolean);
  const controlsLocked = actionLoading;

  const canReset = status === "alarm";
  const canStartAuto = !manualMode && !anyUtenzaRunning && status !== "alarm";
  const canStopAuto = status === "on";

  const canStartManual = manualMode && status !== "alarm" && status !== "on";
  const canStopManual = manualMode && status !== "alarm";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStartAuto}
            onClick={() => setAction("start auto")}
            startIcon={
              sentAction === "start auto" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start auto
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStopAuto}
            onClick={() => setAction("stop auto")}
            startIcon={
              sentAction === "stop auto" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Stop auto
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canReset}
            onClick={() => setAction("reset")}
            startIcon={
              sentAction === "reset" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("azioni.reset")}
          </Button>
        </Grid>
      </Grid>

      {utenze.map((utenza, index) => {
        const acceso = statiUtenze[index];

        return (
          <Grid container spacing={1} key={utenza.statoInfo}>
            <Grid item xs={6} md={4}>
              <Button
                size="small"
                variant="contained"
                disabled={controlsLocked || !canStartManual || acceso}
                onClick={() => setAction(utenza.start)}
                startIcon={
                  sentAction === utenza.start && <SmallLoading sx={{ mr: 1 }} />
                }
                fullWidth
              >
                Start {utenza.label}
              </Button>
            </Grid>
            <Grid item xs={6} md={4}>
              <Button
                size="small"
                variant="contained"
                disabled={controlsLocked || !canStopManual || !acceso}
                onClick={() => setAction(utenza.stop)}
                startIcon={
                  sentAction === utenza.stop && <SmallLoading sx={{ mr: 1 }} />
                }
                fullWidth
              >
                Stop {utenza.label}
              </Button>
            </Grid>
          </Grid>
        );
      })}
    </Box>
  );
}
